import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLogo } from '../context/LogoContext';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Shield,
  LayoutDashboard,
  Users,
  Stethoscope,
  Pill,
  Ambulance,
  QrCode,
  ClipboardList,
  Syringe,
  Building2,
  Package,
  Megaphone,
  FileText,
  AlertTriangle,
  ChevronDown,
  LogOut,
  User,
  Menu,
  X,
  Settings,
} from 'lucide-react';

const menuItems = {
  municipal_admin: [
    { icon: LayoutDashboard, label: 'Command Center', path: '/municipal' },
    { icon: Users, label: 'Barangays', path: '/municipal/barangays' },
    { icon: Stethoscope, label: 'Health Records', path: '/municipal/health' },
    { icon: Pill, label: 'Medicine Inventory', path: '/municipal/medicines' },
    { icon: Ambulance, label: 'Emergency Dispatch', path: '/municipal/incidents' },
    { icon: QrCode, label: 'QR Management', path: '/municipal/households' },
    { icon: Syringe, label: 'Vaccinations', path: '/municipal/vaccinations' },
    { icon: Building2, label: 'Evacuation', path: '/municipal/evacuation' },
    { icon: Package, label: 'Relief & Requests', path: '/municipal/requests' },
    { icon: Megaphone, label: 'Announcements', path: '/municipal/announcements' },
    { icon: AlertTriangle, label: 'Disease Alerts', path: '/municipal/alerts' },
    { icon: FileText, label: 'Audit Logs', path: '/municipal/audit' },
    { icon: Settings, label: 'Settings', path: '/municipal/settings' },
  ],
  barangay_admin: [
    { icon: LayoutDashboard, label: 'Command Center', path: '/barangay' },
    { icon: Stethoscope, label: 'Health Logbook', path: '/barangay/health' },
    { icon: Pill, label: 'Medicine Inventory', path: '/barangay/medicines' },
    { icon: Ambulance, label: 'Emergencies', path: '/barangay/incidents' },
    { icon: QrCode, label: 'Households', path: '/barangay/households' },
    { icon: ClipboardList, label: 'BHW Tasks', path: '/barangay/tasks' },
    { icon: Syringe, label: 'Vaccinations', path: '/barangay/vaccinations' },
    { icon: Building2, label: 'Evacuation', path: '/barangay/evacuation' },
    { icon: Package, label: 'Requests', path: '/barangay/requests' },
    { icon: Megaphone, label: 'Announcements', path: '/barangay/announcements' },
    { icon: AlertTriangle, label: 'Alerts', path: '/barangay/alerts' },
  ],
  bhw: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/bhw' },
    { icon: ClipboardList, label: 'My Tasks', path: '/bhw/tasks' },
    { icon: Stethoscope, label: 'Health Logbook', path: '/bhw/health' },
    { icon: Syringe, label: 'Vaccinations', path: '/bhw/vaccinations' },
    { icon: Users, label: 'Households', path: '/bhw/households' },
  ],
  emt: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/emt' },
    { icon: Ambulance, label: 'Active Incidents', path: '/emt/incidents' },
  ],
};

export const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { logoUrl } = useLogo();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const items = menuItems[user?.role] || [];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Logo component that adapts to uploaded logo or shows default
  const LogoBrand = ({ className = "" }) => (
    <div className={`flex items-center gap-2 min-h-[40px] ${className}`}>
      {logoUrl ? (
        <img 
          src={logoUrl} 
          alt="Organization logo" 
          className="max-h-10 max-w-[140px] object-contain"
          data-testid="header-logo"
        />
      ) : (
        <>
          <Shield className="h-7 w-7 text-primary flex-shrink-0" />
          <div className="min-w-0">
            <h1 className="font-bold text-primary font-['Public_Sans'] text-sm leading-tight">Municipal Health</h1>
            <p className="text-xs text-muted-foreground leading-tight">Operations Platform</p>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-slate-200 z-50 flex items-center justify-between px-4">
        <LogoBrand />
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-testid="mobile-menu-toggle"
        >
          {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </header>

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-white border-r border-slate-200 z-40 transform transition-transform duration-200 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="min-h-[64px] flex items-center px-4 py-3 border-b border-slate-200">
          <LogoBrand />
        </div>

        <ScrollArea className="h-[calc(100vh-8rem)]">
          <nav className="p-4 space-y-1">
            {items.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </ScrollArea>

        {/* User Menu */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200 bg-white">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="w-full justify-between h-auto py-2"
                data-testid="user-menu-trigger"
              >
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-4 w-4 text-primary" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium">{user?.full_name}</p>
                    <p className="text-xs text-muted-foreground capitalize">
                      {user?.role?.replace('_', ' ')}
                    </p>
                  </div>
                </div>
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>My Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="mr-2 h-4 w-4" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        <div className="p-6 lg:p-8">{children}</div>
      </main>
    </div>
  );
};
